<template lang="pug">

div

    .ui.basic.cards

        //- Rechnungsadresse
        address-card(
            headline="Rechnungsadresse"
            type="billing"
            v-bind:address-model="billingAddressModel"
            v-bind:address-models="billingAddressModels"
            v-bind:company-model="companyModel"
            v-bind:country-models="countryModels"
            v-bind:force-billing-address="true"
            v-bind:person-models="personModels"
            v-on:save="saveBillingAddress"
        )

        //- Korrespondenzadresse
        address-card(
            headline="Korrespondenzadresse"
            type="correspondence"
            v-bind:address-model="correspondenceAddressModel"
            v-bind:address-models="correspondenceAddressModels"
            v-bind:company-model="companyModel"
            v-bind:country-models="countryModels"
            v-bind:force-correspondence-address="true"
            v-bind:is-nullable="true"
            v-bind:person-models="personModels"
            v-on:save="saveCorrespondenceAddress"
        )

    .ui.divider

    //- Lieferadressen
    .ui.basic.cards
        shipping-address-card(
            v-for="shippingAddressModel in shippingAddressModels"
            v-bind:key="shippingAddressModel.id"
            v-bind:address-models="shippingAddressAddressModels"
            v-bind:company-model="companyModel"
            v-bind:country-models="countryModels"
            v-bind:is-deletable="shippingAddressModels.length > 1"
            v-bind:person-models="personModels"
            v-bind:shipping-address-model="shippingAddressModel"
        )

        create-shipping-address-card(
            v-show="shippingAddressModels.length < 5"
            v-bind:address-models='shippingAddressAddressModels'
            v-bind:company-model='companyModel'
            v-bind:country-models='countryModels'
            v-bind:person-models='personModels'
            v-bind:product-model='productModel'
        )

    .ui.divider

    //- Belegadressen
    .ui.basic.cards
        specimen-copy-address-card(
            v-bind:address-models='shippingAddressAddressModels'
            v-bind:company-model='companyModel'
            v-bind:country-models='countryModels'
            v-bind:is-internal='false'
            v-bind:person-models='personModels'
            v-bind:recipient-person-models='personModels'
            v-bind:product-model='productModel'
            v-bind:specimen-copy-model='specimenCopyModel'
        )

        specimen-copy-address-card(
            v-if='$auth.isZmuekish'
            v-bind:address-models='zmuekAddressModels'
            v-bind:company-model='zmuekCompanyModel'
            v-bind:country-models='countryModels'
            v-bind:is-internal='true'
            v-bind:person-models='zmuekPersonModels'
            v-bind:recipient-person-models='mediaProductionPersonModels'
            v-bind:product-model='productModel'
            v-bind:specimen-copy-model='specimenCopyInternalModel'
        )

</template>

<script>
    import Vue from 'vue'
    import * as R from 'ramda'
    import store from '@/store'
    import AddressCard from '@/components/addresses/AddressCard'
    import CreateShippingAddressCard from '@/components/addresses/CreateShippingAddressCard'
    import ShippingAddressCard from '@/components/addresses/ShippingAddressCard'
    import SpecimenCopyAddressCard from '@/components/addresses/SpecimenCopyAddressCard'

    // noinspection JSUnusedGlobalSymbols
    // noinspection JSUnusedLocalSymbols
    export default {

        async beforeRouteEnter(to, from, next) {
            const productId = Number(to.params.id)

            const [ productModel, shippingAddressModels ] = await Promise.all([
                // Produkt laden
                store.dispatch('products/loadOne', { id: productId }),

                // Lieferadressen des Produkts laden
                store.dispatch('shippingAddresses/loadAllByProductId', { productId }),

                // Zmuek-Adressen laden (wenn eingeloggte Benutzer Rechte dazu hat)
                R.when(R.equals(true),
                    () => store.dispatch('addresses/loadAllByBelongsToZmuekCompany'),
                )(Vue.$auth.isZmuekish),

                // Zmuek-Unternehmen laden (wenn eingeloggte Benutzer Rechte dazu hat)
                R.when(R.equals(true),
                    () => store.dispatch('companies/loadZmuek')
                )(Vue.$auth.isZmuekish),

                // Zmuek-Personen laden
                store.dispatch('persons/loadAllByBelongsToZmuekCompany'),

                // Alle Medienproduktioner und deren Personen laden (für hausinternes Empfänger-Dropdown)
                store.dispatch('users/loadAllByHasMediaProductionRole').then(() => {
                    const users = store.getters['users/allByHasMediaProductionRole']
                    return Promise.all(
                        users.map(user => store.dispatch('persons/loadOne', { id: user.person }))
                    )
                }),

                // Laender laden
                store.dispatch('countries/loadAll')
            ])

            await Promise.all([
                // Adressen des Produkts laden
                store.dispatch('addresses/loadAllByCompanyId', { companyId: productModel.company }),

                // Unternehmen des Produkts laden
                store.dispatch('companies/loadOne', { id: productModel.company }),

                // Personen des Produkt-Unternehmens laden
                store.dispatch('persons/loadAllByCompanyId', { companyId: productModel.company }),

                // Adressen der Lieferadressen laden
                R.compose(

                    // Promises zusammenfasen
                    // > [ Promise, Promise ]
                    // < Promise
                    promises => Promise.all(promises),

                    // Adressen laden
                    // > [ 1, 2 ]
                    // < [ Promise, Promise ]
                    R.map(addressId => store.dispatch('addresses/loadOne', { id: addressId })),

                    // Address-IDs holen
                    // > [ { id: 1, amount: 100 }, { id: 2, amount: 150 }]
                    // < [ 1, 2 ]
                    R.map(R.prop('address'))

                )(shippingAddressModels),

                // ggf. Belegkopien laden
                R.when(R.is(Number),
                    id => store.dispatch('specimenCopies/loadOne', { id }),
                )(productModel.specimenCopy),

                // ggf. Belegkopien (Intern) laden
                R.when(R.is(Number),
                    id => store.dispatch('specimenCopies/loadOne', { id }),
                )(productModel.specimenCopyInternal),

            ])

            next()
        },

        components: { AddressCard, CreateShippingAddressCard, ShippingAddressCard, SpecimenCopyAddressCard },

        computed: {

            billingAddressModel() {
                return store.getters['addresses/byId'](this.productModel.billingAddress)
            },

            billingAddressModels() {
                return R.compose(

                    // ggf. Default-Array zurueckgeben
                    // > undefined
                    // < []
                    R.defaultTo([]),

                    // Rechnungsadressen des Unternehmens holen
                    // > 1
                    // < [ { id: 2, ... }, { id: 4, ... }, ... ]
                    store.getters['addresses/allByCompanyIdAndIsBillingAddress'],

                )(this.companyModel.id)
            },

            companyModel() {
                return store.getters['companies/byId'](this.productModel.company)
            },

            countryModels() {
                return store.getters['countries/all']
            },

            correspondenceAddressModel() {
                return R.compose(

                    // ggf. Default-Wert setzen
                    // > undefined
                    // < {}
                    R.defaultTo({}),

                    // Adresse holen
                    // > 1
                    // < { id: 1, ... }
                    store.getters['addresses/byId'],

                )(this.productModel.correspondenceAddress)
            },

            correspondenceAddressModels() {
                return store.getters['addresses/allByCompanyIdAndIsCorrespondenceAddress'](this.companyModel.id)
            },

            personModels() {
                return store.getters['persons/allByCompanyId'](this.companyModel.id)
            },

            mediaProductionPersonModels() {
                return store.getters['users/allByHasMediaProductionRole']
                    .map(user => store.getters['persons/byId'](user.person))
            },

            productId() {
                return Number(this.$route.params.id)
            },

            productModel() {
                return store.getters['products/byId'](this.productId)
            },

            shippingAddressAddressModels() {
                return store.getters['addresses/allByCompanyIdAndIsShippingAddress'](this.companyModel.id)
            },

            shippingAddressModels() {
                // Lieferadressen holen
                // > [ 1, 2 ]
                // < [ { id: 1, ... }, { id: 2, ... } ]
                return R.map(store.getters['shippingAddresses/byId'], this.productModel.shippingAddresses)
            },

            specimenCopyModel() {
                return R.compose(

                    // ggf. Defaultwert setzen
                    // > undefined
                    // < {}
                    R.defaultTo({}),

                    // ggf. Belegexemplar holen
                    // > 1
                    // < { id: 1, ... }
                    R.when(R.is(Number), store.getters['specimenCopies/byId']),

                )(this.productModel.specimenCopy)
            },

            specimenCopyInternalModel() {
                return R.compose(

                    // ggf. Defaultwert setzen
                    // > undefined
                    // < {}
                    R.defaultTo({}),

                    // ggf. Belegexemplar laden
                    // > 1
                    // < { id: 1, ... }
                    R.when(R.is(Number), store.getters['specimenCopies/byId']),

                )(this.productModel.specimenCopyInternal)
            },

            zmuekAddressModels() {
                return store.getters['addresses/allByBelongsToZmuek']
            },

            zmuekCompanyModel() {
                return store.getters['companies/isZmuek']
            },

            zmuekPersonModels() {
                return store.getters['persons/allByBelongsToZmuek']
            },

        },

        methods: {

            async saveBillingAddress(addressModel) {
                try {
                    // Model erstellen
                    const model = R.assoc('billingAddress', addressModel.id, this.productModel)

                    // Speichern
                    await store.dispatch('products/update', { id: model.id, model })

                    // Erfolgsmeldung anzeigen
                    this.$growl.success('Rechnungsadresse wurde geändert')

                } catch(e) {
                    // Fehlermeldung anzeigen
                    this.$growl.fatal('Rechnungsadresse konnte nicht geändert werden.', e)
                }
            },

            async saveCorrespondenceAddress(addressModel) {
                const addressId = R.compose(

                    // Standardwert auf ´null´ setzen
                    // > undefined
                    // < null
                    R.defaultTo(null),

                    // ID lesen
                    // > { id: 1, ... }
                    // < 1
                    R.prop('id'),

                    // ggf. Standardwerte setzen
                    // > undefined
                    // < {}
                    R.defaultTo({})

                )(addressModel)



                try {
                    // Model erstellen
                    const model = R.assoc('correspondenceAddress', addressId, this.productModel)

                    // Speichern
                    await store.dispatch('products/update', { id: model.id, model })

                    // Erfolgsmeldung anzeigen
                    this.$growl.success('Korrespondenzadresse wurde geändert')

                } catch(e) {
                    // Fehlermeldung anzeigen
                    this.$growl.fatal('Korrespondenzadresse konnte nicht geändert werden.', e)
                }
            }

        }

    }

</script>

